import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import {
  hasBookingDraftInvoice,
} from "@/lib/bookingInvoiceStatus";
import { hasMissingInvoice, hasOpenInvoiceAmount } from "@/lib/bookingInvoiceDisplay";
import { canWriteInvoice } from "@/lib/permissions";
import type { Booking, BookingDeparture, Invoice, InvoiceListResponse } from "@/types/camping";

export interface UseGuestStayInvoiceStateOptions {
  booking: Booking | BookingDeparture | null;
  open: boolean;
  userGroups?: string[];
}

export function useGuestStayInvoiceState({
  booking,
  open,
  userGroups = [],
}: UseGuestStayInvoiceStateOptions) {
  const canInvoice = canWriteInvoice(userGroups);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [openedInvoiceId, setOpenedInvoiceId] = useState<string | null>(null);
  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);

  const loadInvoices = useCallback(() => {
    if (!booking?.id || !open || !canInvoice) {
      setInvoices([]);
      return;
    }
    setLoading(true);
    api
      .get<InvoiceListResponse>(`/api/v1/invoices?booking_id=${booking.id}`)
      .then((res) => setInvoices(res.items))
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  }, [booking?.id, open, canInvoice]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    if (!open) {
      setOpenedInvoiceId(null);
      setCreateInvoiceOpen(false);
    }
  }, [open]);

  const activeInvoices = useMemo(
    () => invoices.filter((inv) => inv.status !== "cancelled"),
    [invoices],
  );

  const hasDraftInvoice = booking ? hasBookingDraftInvoice(booking) : false;
  const hasUnpaid = booking ? hasOpenInvoiceAmount(booking) : false;

  const hasNoInvoice = useMemo(() => {
    if (!booking) return false;
    if (hasUnpaid || hasDraftInvoice) return false;
    if ("invoice_count" in booking) {
      return hasMissingInvoice(booking);
    }
    if (!canInvoice || loading) return false;
    return activeInvoices.length === 0;
  }, [booking, hasUnpaid, hasDraftInvoice, canInvoice, loading, activeInvoices.length]);

  const canCreateInvoice =
    canInvoice &&
    hasNoInvoice &&
    (booking?.status === "checked_in" || booking?.status === "checked_out");

  const resumeDraftInvoice = useCallback(() => {
    if (!booking?.draft_invoice_id) return;
    setOpenedInvoiceId(booking.draft_invoice_id);
  }, [booking?.draft_invoice_id]);

  const openCreateInvoice = useCallback(() => {
    setCreateInvoiceOpen(true);
  }, []);

  const handleInvoiceCreated = useCallback((invoiceId: string) => {
    setCreateInvoiceOpen(false);
    setOpenedInvoiceId(invoiceId);
    loadInvoices();
  }, [loadInvoices]);

  return {
    canInvoice,
    hasNoInvoice,
    canCreateInvoice,
    hasDraftInvoice,
    openedInvoiceId,
    setOpenedInvoiceId,
    createInvoiceOpen,
    setCreateInvoiceOpen,
    resumeDraftInvoice,
    openCreateInvoice,
    handleInvoiceCreated,
    reloadInvoices: loadInvoices,
  };
}
